import { supportHeaders, supportContext, noteFailure } from './support/context';
import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL } from './constants';
import { getClerkToken } from './auth/clerk';
import {
  DataDomain,
  Perspective,
  SemanticModel,
  MetricDetail,
  DimensionDetail,
  QueryResult,
  QueryFilter,
  QueryTemplate,
  SavedModel,
  QuerySelection,
  MetricConfig,
  OrderBySpec,
  CalculatedAttribute,
  ColumnStat,
  ModelDependents,
  FieldUsage,
  DependencyGraph,
  SchemaDriftReport,
  ConversationThread,
  ReviewDoc,
  ReviewSection,
  ReviewSchedule,
  ReviewWindowRule,
  ReconcileInputClaim,
  ReconcileResult,
  ReconcileSample,
  ReportSource,
} from './types';
import type {
  DraftModel,
  DraftModelSummary,
  DraftVersion,
  InheritedFieldsResponse,
  ValidationResult,
  PublishPreview,
  DiscoveryReviewItem,
  DiscoveryConfirmText,
  DiscoveryCodes,
} from './studioTypes';
import type {
  Dashboard,
  DashboardDataset,
  DashboardVisualization,
  DashboardFilter,
  DatasetData,
  CreateDatasetBody,
  RecommendationsData,
  RglLayoutItem,
  GridPos,
} from './dashboards/dashboardTypes';
import type { ChartSpec } from './dashboards/chartSpec';

// ============================================================
// Axios client instance
// ============================================================

/**
 * What this client understands of the disclosure contract (#426 r27).
 *
 * A deploy is not atomic, so the server cannot assume the client in front of it is this one: while a
 * rollout is going out, and for as long as an open tab survives it, the new server answers the client
 * that shipped before it. The server therefore sends a withheld run's qualification in the field the
 * OLD client renders (`error`) unless the client says otherwise — and this is the client saying
 * otherwise: it renders the typed `withheld` field itself (`Workflows/runExplanation.explainRun`), so it
 * asks for the contract as written rather than the compatibility rendering of it.
 *
 * Removing the compatibility path is one deploy away once no client without this header is left — and
 * "none left" is measured, not assumed: every server worker counts the responses it shapes with and
 * without this header and reports the counters hourly; the exit condition (an observation window of at
 * least 14 days with `undeclared=0` on every worker) is written beside them in
 * `workflows/audience.py`. Remove this header only AFTER the server has stopped reading it.
 */
export const DISCLOSURE_HEADER = 'X-Voyager-Disclosure';
export const DISCLOSURE_CAPABILITIES = 'withheld';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    [DISCLOSURE_HEADER]: DISCLOSURE_CAPABILITIES,
  },
});

// Request interceptor for logging / auth tokens
apiClient.interceptors.request.use(
  async (config) => {
    // Clerk session token when user management is enabled; legacy
    // localStorage token otherwise.
    Object.assign(config.headers, supportHeaders());
    const token = (await getClerkToken()) ?? localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — unwrap the backend { success, data } envelope
apiClient.interceptors.response.use(
  (response) => {
    // If the response follows our { success, data } envelope, unwrap it
    if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
      response.data = response.data.data;
    }
    return response;
  },
  (error) => {
    if (!axios.isCancel(error) && error.config?.headers?.['X-Voyager-Page'] === supportContext().page_id) {
      noteFailure('network', error.response?.headers?.['x-request-id']);
    }
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.error || data?.message || error.message;
      console.error(`API Error [${status}]: ${message}`);
    } else if (error.request) {
      console.error('API Error: No response received from server');
    } else {
      console.error('API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ============================================================
// Domain & Perspective APIs
// ============================================================

/** Fetch all available data domains. */
export async function getDomains(): Promise<DataDomain[]> {
  const { data } = await apiClient.get<DataDomain[]>('/api/domains');
  return data;
}

/** Fetch perspectives for a given domain. */
export async function getPerspectives(domain: string): Promise<Perspective[]> {
  const { data } = await apiClient.get<Perspective[]>('/api/perspectives', {
    params: { domain },
  });
  return data;
}

/** Fetch all perspectives across all domains. */
export async function getAllPerspectives(): Promise<Perspective[]> {
  const { data } = await apiClient.get<Perspective[]>('/api/perspectives');
  return data;
}

// ============================================================
// Semantic Model APIs
// ============================================================

/** Fetch the full semantic model for a perspective. */
export async function getModel(
  perspective: string,
  params?: { source?: string; draftId?: string }
): Promise<SemanticModel> {
  const { data } = await apiClient.get<SemanticModel>(
    `/api/model/${encodeURIComponent(perspective)}`,
    { params }
  );
  return data;
}

/** Fetch details for a specific node (metric or dimension) within a perspective model. */
export async function getModelNode(
  perspective: string,
  nodeId: string
): Promise<MetricDetail | DimensionDetail> {
  const { data } = await apiClient.get<MetricDetail | DimensionDetail>(
    `/api/model/${encodeURIComponent(perspective)}/node/${encodeURIComponent(nodeId)}`
  );
  return data;
}

// ============================================================
// Query APIs
// ============================================================

/** Generate SQL from selected metrics, dimensions, and filters. */
export async function generateQuery(params: {
  perspective: string;
  metrics: (string | { name: string; model?: string; instanceId?: string })[];
  dimensions: (string | { name: string; model?: string })[];
  filters: QueryFilter[];
  metricConfigs?: MetricConfig[];
  calculatedAttributes?: CalculatedAttribute[];
  limit?: number;
  draftId?: string;
  dateField?: string;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  dateRangePreset?: string;
  orderBy?: OrderBySpec[];
}): Promise<{ sql: string; meta?: { sqlDialect?: string } & Record<string, unknown>; readings?: QueryResult['readings'] }> {
  const { data } = await apiClient.post<{ sql: string; meta?: { sqlDialect?: string } & Record<string, unknown>; readings?: QueryResult['readings'] }>(
    '/api/query/generate',
    params
  );
  return data;
}

/** Execute a SQL query and return the results.
 *
 * `context` is an opaque snapshot of the workspace state at request time.
 * The backend echoes it into its error logs on failure, so server-side
 * triage matches the frontend's diagnostic report.
 */
export async function executeQuery(
  sql: string,
  context?: Record<string, unknown>,
  sqlDialect?: string
): Promise<QueryResult> {
  const { data } = await apiClient.post<QueryResult>('/api/query/execute', {
    sql,
    ...(context ? { context } : {}),
    // Dialect the generated SQL is written in (meta.sqlDialect from /generate)
    // so warehouse-backed queries are transpiled exactly once server-side.
    ...(sqlDialect ? { sqlDialect } : {}),
  }, { timeout: 330000 }); // up to 300s warehouse execution plus response overhead
  return data;
}

// ============================================================
// Templates APIs
// ============================================================

/** Fetch available query templates. */
export async function getTemplates(): Promise<QueryTemplate[]> {
  const { data } = await apiClient.get<QueryTemplate[]>('/api/templates');
  return data;
}

// ============================================================
// Report APIs (replaces Saved Models)
// ============================================================

/** Fetch all reports with optional filters. */
export async function getReports(params?: {
  created_by?: string;
  perspective?: string;
  isTemplate?: boolean;
}): Promise<SavedModel[]> {
  const { data } = await apiClient.get<SavedModel[]>('/api/saved-models', {
    params,
  });
  return data;
}

/** Save a new report. */
export async function saveReport(report: {
  name: string;
  description: string;
  domain: string;
  perspective: string;
  selection: QuerySelection;
  created_by: string;
  isTemplate?: boolean;
  templateTags?: string[];
}): Promise<SavedModel> {
  const { data } = await apiClient.post<SavedModel>('/api/saved-models', report);
  return data;
}

/** Fetch a specific report by ID. */
export async function getReport(id: string): Promise<SavedModel> {
  const { data } = await apiClient.get<SavedModel>(
    `/api/saved-models/${encodeURIComponent(id)}`
  );
  return data;
}

/** Update an existing report by ID. */
export async function updateReport(
  id: string,
  report: {
    name: string;
    description: string;
    domain: string;
    perspective: string;
    selection: QuerySelection;
    isTemplate?: boolean;
    templateTags?: string[];
  }
): Promise<SavedModel> {
  const { data } = await apiClient.put<SavedModel>(
    `/api/saved-models/${encodeURIComponent(id)}`,
    report
  );
  return data;
}

/** Delete a report by ID. */
export async function deleteReport(id: string): Promise<void> {
  await apiClient.delete(`/api/saved-models/${encodeURIComponent(id)}`);
}

// Deprecated aliases for backward compatibility
export const getSavedModels = getReports;
export const saveModel = (model: {
  name: string;
  description: string;
  domain: string;
  perspective: string;
  selection: QuerySelection;
}) => saveReport({ ...model, created_by: 'anonymous' });
export const getSavedModel = getReport;

// ============================================================
// Studio APIs
// ============================================================

/** Fetch all draft summaries. */
export async function getStudioDrafts(): Promise<DraftModelSummary[]> {
  const { data } = await apiClient.get<DraftModelSummary[]>('/api/studio/drafts');
  return data;
}

/** Fetch a full draft by ID. */
export async function getStudioDraft(id: string): Promise<DraftModel> {
  const { data } = await apiClient.get<DraftModel>(
    `/api/studio/drafts/${encodeURIComponent(id)}`
  );
  return data;
}

/** Create a new draft. */
export async function createStudioDraft(params: {
  name: string;
  metadata?: Partial<DraftModel['metadata']>;
  created_by?: string;
}): Promise<DraftModel> {
  const { data } = await apiClient.post<DraftModel>('/api/studio/drafts', params);
  return data;
}

/** Update an existing draft. */
export async function updateStudioDraft(
  id: string,
  draft: Partial<DraftModel> & { updated_by?: string }
): Promise<DraftModel> {
  const { data } = await apiClient.put<DraftModel>(
    `/api/studio/drafts/${encodeURIComponent(id)}`,
    draft
  );
  return data;
}

/** Delete a draft. */
export async function deleteStudioDraft(id: string): Promise<void> {
  await apiClient.delete(`/api/studio/drafts/${encodeURIComponent(id)}`);
}

/** Create a new draft from an existing semantic model. */
export async function loadDraftFromModel(modelName: string, username?: string): Promise<DraftModel> {
  const { data } = await apiClient.get<DraftModel>(
    `/api/studio/drafts/from-model/${encodeURIComponent(modelName)}`,
    { params: username ? { username } : undefined }
  );
  return data;
}

/** Fetch the current production model as a DraftModel snapshot (read-only, not persisted). */
export async function getProductionSnapshot(modelName: string): Promise<DraftModel | null> {
  try {
    const { data } = await apiClient.get<DraftModel>(
      `/api/studio/production-snapshot/${encodeURIComponent(modelName)}`
    );
    return data;
  } catch {
    return null; // Model may not exist in production (brand-new draft)
  }
}

/** Validate a draft model. */
export async function validateStudioModel(draft: DraftModel): Promise<ValidationResult> {
  const { data } = await apiClient.post<ValidationResult>('/api/studio/validate', draft);
  return data;
}

/** Preview a draft publish (dry-run — no files written). */
export async function previewStudioPublish(id: string): Promise<PublishPreview> {
  const { data } = await apiClient.post<PublishPreview>(
    `/api/studio/drafts/${encodeURIComponent(id)}/preview-publish`
  );
  return data;
}

/** Discover review (#1031): the draft's needs_person explorer items, as the server renders them, and the hash of
 * the draft version they were rendered from. */
export async function getDiscoveryReview(id: string): Promise<{ items: DiscoveryReviewItem[]; content_hash: string }> {
  const { data } = await apiClient.get<{ items: DiscoveryReviewItem[]; content_hash: string }>(
    `/api/studio/drafts/${encodeURIComponent(id)}/review`
  );
  return data;
}

/** A person confirms one Discover item with their own words and the code values they saw (#1194), against the draft
 * version they reviewed. */
export async function confirmDiscoveryItem(
  id: string, reviewedHash: string, ref: string, text: DiscoveryConfirmText, codes: DiscoveryCodes,
): Promise<void> {
  await apiClient.post(`/api/studio/drafts/${encodeURIComponent(id)}/confirm`, { ref, text, codes, reviewed_hash: reviewedHash });
}

/** A person confirms every item filtered on one code column, all or nothing, against the version they reviewed. */
export async function confirmDiscoveryBatch(
  id: string, reviewedHash: string, column: string, items: Array<{ ref: string; text: DiscoveryConfirmText; codes: DiscoveryCodes }>,
): Promise<void> {
  await apiClient.post(`/api/studio/drafts/${encodeURIComponent(id)}/confirm-batch`, { column, items, reviewed_hash: reviewedHash });
}

/** Publish a draft model. */
export async function publishStudioModel(id: string): Promise<{ filesWritten: string[]; perspectiveId: string; draft: DraftModel }> {
  const { data } = await apiClient.post(
    `/api/studio/drafts/${encodeURIComponent(id)}/publish`
  );
  return data;
}

/** AI join suggestion. */
export async function studioSuggestJoins(tables: DraftModel['tables']): Promise<DraftModel['relationships']> {
  const { data } = await apiClient.post('/api/studio/suggest/joins', { tables });
  return data;
}

/** AI display name generation. */
export async function studioSuggestDisplayNames(columns: string[]): Promise<{ name: string; display_name: string }[]> {
  const { data } = await apiClient.post('/api/studio/suggest/display-names', { columns });
  return data;
}

/** AI column classification. */
export async function studioClassifyColumns(columns: { name: string; type: string }[]): Promise<{ name: string; type: string; classification: string }[]> {
  const { data } = await apiClient.post('/api/studio/suggest/classify', { columns });
  return data;
}

// ============================================================
// Studio Version History & Inherited Fields APIs
// ============================================================

/** Fetch version history for a draft (requires Postgres backend). */
export async function getDraftVersions(draftId: string): Promise<DraftVersion[]> {
  const { data } = await apiClient.get<DraftVersion[]>(
    `/api/studio/drafts/${encodeURIComponent(draftId)}/versions`
  );
  return data;
}

/** Fetch a specific version of a draft (requires Postgres backend). */
export async function getDraftVersion(draftId: string, versionNumber: number): Promise<any> {
  const { data } = await apiClient.get(
    `/api/studio/drafts/${encodeURIComponent(draftId)}/versions/${versionNumber}`
  );
  return data;
}

/** Fetch resolved inherited dimensions/metrics for a model (perspective editor). */
export async function getInheritedFields(modelName: string): Promise<InheritedFieldsResponse> {
  const { data } = await apiClient.get<InheritedFieldsResponse>(
    `/api/studio/inherited-fields/${encodeURIComponent(modelName)}`
  );
  return data;
}

// ============================================================
// Workspace Draft Mode APIs
// ============================================================

/** Fetch draft summaries for a specific model (for Workspace draft picker). */
export async function getDraftsForModel(modelName: string): Promise<DraftModelSummary[]> {
  const { data } = await apiClient.get<DraftModelSummary[]>(
    `/api/studio/drafts-for-model/${encodeURIComponent(modelName)}`
  );
  return data;
}

// ============================================================
// Impact Analysis APIs
// ============================================================

/** Fetch direct/transitive dependents and affected perspectives for a model. */
export async function getModelDependents(modelName: string): Promise<ModelDependents> {
  const { data } = await apiClient.get<ModelDependents>(
    `/api/impact/models/${encodeURIComponent(modelName)}/dependents`
  );
  return data;
}

/** Fetch usage of a specific field across models, saved reports, and formulas. */
export async function getFieldUsage(
  modelName: string,
  fieldName: string,
  fieldType: string
): Promise<FieldUsage> {
  const { data } = await apiClient.get<FieldUsage>(
    `/api/impact/models/${encodeURIComponent(modelName)}/fields/${encodeURIComponent(fieldName)}/usage`,
    { params: { type: fieldType } }
  );
  return data;
}

/** Schema drift: each physical table the semantic layer declares, against the live warehouse (503 when it cannot be reached). */
export async function getSchemaDrift(): Promise<SchemaDriftReport> {
  const { data } = await apiClient.get<SchemaDriftReport>('/api/impact/schema-drift');
  return data;
}

/** Fetch the full dependency graph with model summaries. */
export async function getDependencyGraph(): Promise<DependencyGraph> {
  const { data } = await apiClient.get<DependencyGraph>('/api/impact/graph');
  return data;
}

// ============================================================
// Studio Agent (NL → StudioActions)
// ============================================================

// ============================================================
// Dashboard APIs
// ============================================================

/** List the current user's dashboards. */
export async function listDashboards(): Promise<Dashboard[]> {
  const { data } = await apiClient.get<Dashboard[]>('/api/dashboards/');
  return data;
}

/** Create a new dashboard. */
export async function createDashboard(body: {
  name: string;
  description?: string;
  layout?: RglLayoutItem[];
}): Promise<Dashboard> {
  const { data } = await apiClient.post<Dashboard>('/api/dashboards/', body);
  return data;
}

/** Fetch a dashboard with its datasets and visualizations. */
export async function getDashboard(id: string): Promise<Dashboard> {
  const { data } = await apiClient.get<Dashboard>(`/api/dashboards/${encodeURIComponent(id)}`);
  return data;
}

/** Update dashboard metadata / layout. */
export async function updateDashboard(
  id: string,
  body: { name?: string; description?: string; layout?: RglLayoutItem[]; filters?: DashboardFilter[] }
): Promise<Dashboard> {
  const { data } = await apiClient.patch<Dashboard>(`/api/dashboards/${encodeURIComponent(id)}`, body);
  return data;
}

/** Delete a dashboard (cascades to its datasets + visualizations). */
export async function deleteDashboard(id: string): Promise<void> {
  await apiClient.delete(`/api/dashboards/${encodeURIComponent(id)}`);
}

/** Add a dataset (saved_report | sql | nl) to a dashboard. */
export async function createDataset(dashboardId: string, body: CreateDatasetBody): Promise<DashboardDataset> {
  const { data } = await apiClient.post<DashboardDataset>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/datasets`,
    body
  );
  return data;
}

/** Delete a dataset. */
export async function deleteDataset(dashboardId: string, datasetId: string): Promise<void> {
  await apiClient.delete(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/datasets/${encodeURIComponent(datasetId)}`
  );
}

/** Execute a dataset's stored query and return rows (optionally dashboard-filtered). */
export async function getDatasetData(
  dashboardId: string,
  datasetId: string,
  filters?: DashboardFilter[]
): Promise<DatasetData> {
  const { data } = await apiClient.post<DatasetData>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/datasets/${encodeURIComponent(datasetId)}/data`,
    filters && filters.length ? { filters } : {}
  );
  return data;
}

/** Profile a dataset and return ranked ChartSpec recommendations.
 * Optional `query` is a natural-language intent for the LLM curator. */
export async function recommendVisualizations(
  dashboardId: string,
  datasetId: string,
  query?: string,
  filters?: DashboardFilter[]
): Promise<{ recommendations: ChartSpec[]; columns: string[]; curated: boolean }> {
  const body: Record<string, unknown> = {};
  if (query && query.trim()) body.query = query.trim();
  if (filters && filters.length) body.filters = filters;
  const { data } = await apiClient.post<{ recommendations: ChartSpec[]; columns: string[]; curated: boolean }>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/datasets/${encodeURIComponent(datasetId)}/recommend`,
    body
  );
  return data;
}

/** Fetch a dataset's SemanticQuery + compiled SQL (for the per-viz query viewer). */
export async function getDatasetSql(
  dashboardId: string,
  datasetId: string
): Promise<{ source_kind: string; semantic_query: Record<string, unknown> | null; raw_sql: string | null; sql: string | null; sql_error?: string }> {
  const { data } = await apiClient.post(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/datasets/${encodeURIComponent(datasetId)}/sql`
  );
  return data;
}

/** Expanded recommendations: the full gallery (browse by chart type) + a composed best-layout. */
export async function getRecommendations(
  dashboardId: string,
  datasetId: string,
  filters?: DashboardFilter[]
): Promise<RecommendationsData> {
  const { data } = await apiClient.post<RecommendationsData>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/datasets/${encodeURIComponent(datasetId)}/recommendations`,
    filters && filters.length ? { filters } : {}
  );
  return data;
}

/** Add a visualization (manual chart_spec) referencing a dataset. */
export async function createVisualization(
  dashboardId: string,
  body: { dataset_id: string; chart_spec: ChartSpec; position?: GridPos }
): Promise<DashboardVisualization> {
  const { data } = await apiClient.post<DashboardVisualization>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/visualizations`,
    body
  );
  return data;
}

/** Update a visualization. */
export async function updateVisualization(
  dashboardId: string,
  vizId: string,
  body: { dataset_id?: string; chart_spec?: ChartSpec; position?: GridPos }
): Promise<DashboardVisualization> {
  const { data } = await apiClient.patch<DashboardVisualization>(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/visualizations/${encodeURIComponent(vizId)}`,
    body
  );
  return data;
}

/** Delete a visualization. */
export async function deleteVisualization(dashboardId: string, vizId: string): Promise<void> {
  await apiClient.delete(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/visualizations/${encodeURIComponent(vizId)}`
  );
}

export async function studioAgentChat(params: {
  message: string;
  conversationHistory: { role: 'user' | 'assistant'; content: string }[];
  draftModel: DraftModel | null;
  thread_id?: string | null;
}): Promise<{
  reply: string;
  thinking?: string;
  actions?: import('./studioTypes').StudioActions;
  suggestedFollowUps?: string[];
  clarifyingQuestions?: string[];
  session_id?: string;
}> {
  const { data } = await apiClient.post('/api/studio-agent/chat', params, {
    timeout: 120000,
  });
  return data;
}


// ---------------------------------------------------------------------------
// Diagnose (why engine, #114 / epic #127)
// ---------------------------------------------------------------------------

export interface DiagnoseEdge {
  source: string;
  target: string;
  status: string;
  kind: string;
  confidence: number | null;
  provenance: Record<string, unknown>;
}

export interface DiagnoseChainNode {
  metric: string;
  parent: string;
  delta_pct: number | null;
  z: number;
  score: number;
  edge: DiagnoseEdge;
  depth: number;
}

/**
 * What the last answer in a thread was scoped to (server-written on the
 * ladder envelope): the metric, groupings, filters and window a follow-up
 * inherits when it omits them and replaces when it names them.
 */
export interface ThreadContext {
  perspective?: string | null;
  metrics: string[];
  primary_metric?: string | null;
  dimensions: string[];
  filters: Record<string, any>[];
  window?: { start?: string | null; end?: string | null; preset?: string | null; field?: string | null } | null;
  definitions: string[];
  session_id?: string | null;
  /** The route the router decided for the answer (#760): the next question's previous route. */
  route?: string | null;
}

export interface DiagnosisContinuation {
  parent_session_id: string;
  measurements_recorded_at: string;
  reused_queries: number;
  measurements_refreshed: false;
}

export interface DiagnoseEnvelope {
  continuation?: DiagnosisContinuation;
  target: { metric: string; label?: string; window_start: string; window_end: string; filters: Record<string, unknown>;
    /** The number itself (#188): this window's value, the prior window's, and the move between them. */
    current?: number | null; prior?: number | null; delta?: number | null; trailing_mean?: number | null; direction?: string | null;
    /** How the number is written (percent, currency, count): the claim's format, else the catalog's. */
    format?: string | null;
    maturity?: { days: number; immature_days: number; original_window_end: string; original_delta: number | null; recheck_after?: string } };
  /** The significance step alone — a review popover's first look; the full walk was not run. */
  preview?: boolean;
  verdict: {
    kind: 'explained' | 'unexplained' | 'within_normal' | 'data_late' | 'immature' | 'insufficient_data' | 'error' | 'corrected' | 'preview';
    headline: string;
    confidence: number;
    recheck_after?: string;
    weakest_link?: DiagnoseEdge | null;
    caveat?: string;
    corrected_by?: { id: string | null; name: string | null; at: string | null; correction_id: string | null };
    driver?: string;
    /** The orchestrator's late run behind a `data_late` verdict (#194). */
    late_run?: { id?: string | null; dag_id?: string; run_id?: string; landed?: boolean; ended_at?: string | null; scheduled_at?: string | null;
      lateness_s?: number | null; lateness?: string; reads?: string | null; built_before?: Array<{ dag_id?: string; ended_at?: string | null }> } | null;
  };
  chain: DiagnoseChainNode[];
  best_path?: string[];
  segments: {
    dimension: string;
    top_share: number;
    segments: { segment: string; delta: number }[];
    metric: string;
    method: string;
  } | null;
  annotations: {
    id: string;
    kind: string;
    ts: string;
    ts_end?: string | null;
    title: string;
    body?: string | null;
    source: string;
    source_ref?: string | null;
    match_score: number;
  }[];
  alternatives: { hypothesis: string; status: string; reason: string }[];
  residual_note: string | null;
  budget: { limit: number; queries: number; effort: string };
  /** The walk as a contract (#184): four steps, always in this order, always reported. */
  steps?: DiagnoseStep[];
  /**
   * The steps contract this envelope was produced under (#426). ABSENT means v1 — the four steps,
   * exactly what every client has always received. `2` means the client asked for the five-step
   * walk and the fifth step, `connections`, is in `steps`.
   */
  steps_version?: 2;
  /** The traced session this walk wrote (#184) — the id the Trace button opens. */
  session_id?: string | null;
  /** Every driver with its earned tier and typed sources (#185). */
  drivers?: DiagnoseDriver[];
  /** the walk's evidence ceiling (#297): the first signal-made edge the best path crossed, or null on confirmed ground */
  ceiling?: { capped_by: Record<string, unknown>; capped_metrics: string[]; clean_metrics: string[] } | null;
  /** Corrections layer (#186): the walk's own verdict first, each correction after, the newest is `verdict`. */
  verdict_layers?: Array<{ layer: 'original' | 'correction'; verdict: any; [k: string]: any }>;
  corrections?: Array<Record<string, any>>;
  /** Contributions on this number (#193): the ones the data confirmed on this window are counted in the note. */
  contributions?: Array<{ id: string; by: { id: string | null; name: string | null; at: string | null }; tier: EvidenceTier; validation: ContributionValidation; note: string }>;
  contributions_note?: string | null;
  contributions_other?: number;
  /** What the viewer's evidence policy hid (#187). */
  policy?: PolicyBlock | null;
  activity?: any[];
}

export type DiagnoseStepId = 'significance' | 'boring_checks' | 'attribution' | 'matched_events' | 'connections';

/**
 * The fifth step (#426, Connections P0-7), present only on a `steps_version: 2` envelope.
 *
 * A connection is a tested hypothesis, typed by what was established; the comparisons it rests on and
 * the limitations that qualify it travel WITH it — the server discloses the three as one unit, so a
 * result the reader may see is never shown without its qualifications, and one whose qualification the
 * reader may not read arrives downgraded (`downgraded: true`) with the generic note in its place.
 * Empty in P0: nothing evaluates a connection before P1.
 */
export interface DerivedRow {
  /** the row arrived structurally broken and fails closed wherever it is read */
  malformed?: boolean;
  /** the recorded lineage (#423) every derived row carries — never a credential, re-checked on every read */
  dependencies?: Array<Record<string, any>>;
  /** the row has no external lineage: the walk computed it from the catalog itself. Absence of both is not permission. */
  intrinsic?: boolean;
}
export interface ConnectionLimitation extends DerivedRow {
  /** the vocabulary word that identifies it: no_comparison_group, exposure_undeclared, evidence_unavailable… */
  code: string;
  /** a display copy of a qualification belonging to `origin` — rebuilt by the server on every read, never stored */
  merged?: boolean;
  origin?: string | null;
  /** how it reads to this viewer — null on a persisted copy, whose words are re-read live */
  text?: string | null;
  tier?: EvidenceTier;
  /** the detail was not this viewer's to read: the generic note stands in its place */
  restricted?: boolean;
  test?: string | null;
}
export interface ConnectionComparison extends DerivedRow {
  id: string;
  observation?: string | null;
  populations?: string[];
  tier?: EvidenceTier;
  headline?: string | null;
  note?: string | null;
  /** its own limitations plus the step's — the server sends the complete set, the renderer shows all of it */
  limitations?: ConnectionLimitation[];
  values?: Record<string, any> | null;
  downgraded?: boolean;
}
export interface ConnectionView extends DerivedRow {
  id: string;
  /** coincides · consistent_with · associated_with · arithmetically_contributes · concentrates · explains */
  relation: string;
  from_ref?: string | null;
  to_ref?: string | null;
  assertion?: string | null;
  tier?: EvidenceTier;
  headline?: string | null;
  narrative?: string | null;
  predicate?: Record<string, any> | null;
  sources?: EvidenceSource[];
  /** the ids of the comparisons this connection rests on */
  comparisons?: string[];
  /** the COMPLETE qualification set: its own, its comparisons', the step's. Render all of it. */
  limitations?: ConnectionLimitation[];
  downgraded?: boolean;
}
export interface RejectedCandidate extends DerivedRow {
  id: string;
  assertion?: string | null;
  code?: string | null;
  reason?: string | null;
  tier?: EvidenceTier;
}
export interface ConnectionsDetail {
  /** the discriminator: this object says what it is, so nothing has to guess from its shape */
  view: 'connections_detail';
  view_version: number;
  /** rows normalization threw away (an ambiguous identity, a row that was not a row) — carried, not inferred */
  structural_removals?: number;
  /** the view itself arrived structurally broken; every result in it is qualified by that */
  malformed?: boolean;
  connections: ConnectionView[];
  comparisons: ConnectionComparison[];
  /** step-level limitations: they qualify every result in the step */
  limitations: ConnectionLimitation[];
  rejected: RejectedCandidate[];
}

/** Personas as evidence policy (#187): disclosure, not access. */
export type EvidenceBar = 'conservative' | 'balanced' | 'exploratory';
export interface EvidencePolicy { policy: 'gated' | 'full' | 'facts'; bar: EvidenceBar }
export interface PolicyBlock {
  policy: string; bar?: string | null; min_tier?: string;
  hidden_count?: number; hidden_by_tier?: Record<string, number>;
  why_hidden?: number; next_hidden?: number; next_withheld?: string | null;
  raise_to?: string | null; raised?: boolean;
  /** connections below the bar, counted not shown (#426) — part of `hidden_count`, never double-counted */
  connections_hidden?: number;
  /** a persisted v2 view whose policy was re-applied on this read (#426) */
  reapplied?: boolean;
  /** this read's authorization took rows out of the walk — a flag, never a count */
  access_filtered?: boolean;
}
/** WHAT NEXT v1 (#188): a suggestion anchored to a driver, projected from the number itself. */
export interface LadderSuggestion {
  text: string;
  kind: 'follow_up' | 'draft' | 'watch' | 'automate-later' | string;
  anchored_to?: string | null;
  anchors?: string[];
  driver?: { kind?: string; id?: string | null; label?: string | null; tier?: string } | null;
  projection?: { value: number; unit?: string | null; basis: string; inputs?: Record<string, any> } | null;
  risk?: 'low' | 'medium' | 'high' | null;
  tier?: EvidenceTier | string;
  sources?: EvidenceSource[];
}
export interface Me {
  id: string; display_name: string; email: string | null; org_id: string | null; org_role: string | null;
  preferences: { appearance?: import('./design/ThemeProvider').StromaAppearance } & Record<string, unknown>;
  evidence_policy: EvidencePolicy; can_set_policy: boolean;
  /** deploy-time flags (#464): `domains: false` = the catalog is perspectives only; `simple_ui` is always true (kept for older bundles) */
  features?: { domains?: boolean; simple_ui?: boolean };
  /** may manage who is Voyager support staff (Settings → Voyager support) */
  platform_owner?: boolean;
}
export async function getMe(): Promise<Me> {
  const { data } = await apiClient.get<Me>('/api/me');
  return data;
}
export async function setMyPreferences(body: { appearance: import('./design/ThemeProvider').StromaAppearance }): Promise<Me> {
  const { data } = await apiClient.put<Me>('/api/me/preferences', body);
  return data;
}
export async function setEvidencePolicy(body: { policy: EvidencePolicy['policy']; bar: EvidenceBar; email?: string; user_id?: string }): Promise<Me> {
  const { data } = await apiClient.put<Me>('/api/users/evidence-policy', body);
  return data;
}

/** Evidence tiers (#185): earned on the server, never inferred in the browser. */
export type EvidenceTier = 'confirmed' | 'emerging' | 'unconfirmed';
export interface EvidenceSource {
  type: 'calendar' | 'metrics' | 'pr' | 'doc' | 'meeting' | 'experiment' | 'ticket' | 'pipeline' | string;
  detail: string;
  meta?: Record<string, any>;
  weak?: boolean;
}
export interface DiagnoseDriver {
  kind: 'metric' | 'segment' | 'event' | 'text';
  id: string | null;
  tier: EvidenceTier;
  sources: EvidenceSource[];
  label?: string; delta_pct?: number | null; z?: number | null; edge_status?: string | null;
  dimension?: string; segment?: any; share?: number; metric?: string | null;
  title?: string; ts?: string; annotation_kind?: string | null;
  routed?: boolean;
  /** The provenance of the edge this driver was reached over (#297): `method: 'signal'` means someone's word made it. */
  edge_provenance?: Record<string, any> | null;
  /** A relationship a signal proposed (#297): who, from which claim — the walk says so instead of calling it a driver. */
  proposed_by?: { by?: string | null; origin?: string | null; claim_id?: string | null; at?: string | null } | null;
  /** The path to the target crosses someone's word (#297): this driver is unconfirmed whatever its own edge is. */
  capped_by?: Record<string, any> | null;
  /** Set when a person corrected the verdict to this driver (#186). */
  corrected_by?: { id: string | null; name: string | null; at: string | null; correction_id: string | null } | null;
  /** A contribution (#193): who added it, what the dry run said, the driver it was attached to. */
  added_by?: { id: string | null; name: string | null; at: string | null } | null;
  validation?: ContributionValidation | null;
  attached_to?: { driver_id?: string | null; driver_kind?: string | null; driver_label?: string | null } | null;
}

/** A contribution's dry run (#193): its status, what it found, whether it was run on another window. */
export interface ContributionValidation {
  status?: 'validating' | 'done' | null;
  outcome?: 'passed' | 'no_effect' | 'untestable' | 'error' | null;
  tier?: EvidenceTier | null;
  reason?: string | null;
  effect?: { share?: number; segment_delta_pct?: number | null; ratio_vs_rest?: number | null; direction_agrees?: boolean; sample?: number | null } | null;
  historical?: boolean;
  dimension?: string | null;
  segment?: string | number | null;
  window?: { start: string; end: string } | null;
  run?: number;
}

export interface DiagnoseStep {
  id: DiagnoseStepId;
  label: string;
  status: string;
  summary: string | null;
  detail: Record<string, any>;
}


export interface DiagnosisRun {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled' | 'expired';
  session_id: string | null;
  queries_completed: number;
  queries_started: number;
  message: string | null;
  result?: DiagnoseEnvelope;
}
export interface DiagnosisRunOptions {
  operationId: string;
  onProgress: (run: DiagnosisRun) => void;
  shouldStop: () => boolean;
  isCurrent: () => boolean;
}
export class DiagnosisConnectionError extends Error { readonly reconnectableDiagnosis = true; }

export async function trackDiagnosis(options: DiagnosisRunOptions, body?: unknown): Promise<DiagnoseEnvelope> {
  let current: DiagnosisRun | undefined;
  if (body) {
    try {
      current = (await apiClient.post<DiagnosisRun>('/api/diagnose/runs',
        { ...(body as object), operation_id: options.operationId }, { timeout: 30000 })).data;
    } catch (error: any) {
      if (error?.response && error.response.status < 500 && error.response.status !== 408) throw error;
      // The server may have accepted a request whose response was lost.
    }
  }
  let failures = 0;
  let cancelFailures = 0;
  for (;;) {
    if (!options.isCurrent()) throw new Error('Analysis belongs to another workspace.');
    if (current) {
      options.onProgress(current);
      if (options.shouldStop() && ['queued', 'running'].includes(current.status)) {
        try {
          current = (await apiClient.post<DiagnosisRun>(`/api/diagnose/runs/${encodeURIComponent(current.id)}/cancel`, {}, { timeout: 30000 })).data;
          continue;
        } catch (error: any) {
          if (++cancelFailures >= 3 || [401, 403].includes(error?.response?.status)) {
            throw new DiagnosisConnectionError('Stop is pending. Reconnect to confirm the server has stopped this analysis.');
          }
        }
      }
      if (current.status === 'done' && current.result) return current.result;
      if (['failed', 'expired', 'cancelled'].includes(current.status)) throw new Error(current.message || 'Analysis stopped.');
    }
    await new Promise(resolve => setTimeout(resolve, 2000));
    if (!options.isCurrent()) throw new Error('Analysis belongs to another workspace.');
    try {
      current = (await apiClient.get<DiagnosisRun>(
        `/api/diagnose/operations/${encodeURIComponent(options.operationId)}`, { timeout: 30000 })).data;
      failures = 0;
    } catch (error: any) {
      if ([401, 403].includes(error?.response?.status)) throw error;
      if (++failures >= 3) {
        if (error?.response?.status === 404) {
          if (options.shouldStop()) {
            try {
              current = (await apiClient.post<DiagnosisRun>(
                `/api/diagnose/operations/${encodeURIComponent(options.operationId)}/cancel`, {}, { timeout: 30000 })).data;
              continue;
            } catch {
              throw new DiagnosisConnectionError('Stop is pending. Reconnect to confirm the server has stopped this analysis.');
            }
          }
          throw new Error('Analysis is unavailable or no longer accessible.');
        }
        throw new DiagnosisConnectionError('Connection interrupted. Reconnect to check this analysis without running it again.');
      }
    }
  }
}

/** Run the deterministic why-engine walk for a metric + window. */
export async function runDiagnose(params: {
  continue_session_id?: string | null;
  metric: string;
  window_start: string;
  window_end: string;
  effort?: string;
  filters?: any;
  thread_id?: string | null;
  question?: string;
  /** The conversation's perspective — recorded on the walk's session so a replay selects it again. */
  perspective_id?: string | null;
  /** Raise the evidence bar for THIS answer (#187); not remembered. */
  bar?: EvidenceBar | null;
  /** Opt into the five-step walk (#426): the INTEGER 2 only. Omit for the four-step contract. */
  steps_version?: 2;
}, runOptions?: DiagnosisRunOptions): Promise<DiagnoseEnvelope> {
  if (runOptions) return trackDiagnosis(runOptions, params);
  // NB: the response interceptor already unwraps { success, data }
  const { data } = await apiClient.post<DiagnoseEnvelope>(
    '/api/diagnose/',
    params,
    { timeout: 180000 }
  );
  return data;
}

/**
 * WHY from a number (#184): the popover posts the claim it has — grounding
 * and all — and the server turns it into the walk's target. Nothing is
 * re-parsed from words.
 */
export async function runDiagnoseForClaim(
  claim: unknown,
  opts: { effort?: string; thread_id?: string | null; question?: string; perspective_id?: string | null; bar?: EvidenceBar | null;
    /** The significance step only: is the move worth a walk? Nothing is explained. */
    preview?: boolean } = {},
  runOptions?: DiagnosisRunOptions,
): Promise<DiagnoseEnvelope> {
  if (runOptions) return trackDiagnosis(runOptions, { claim, ...opts });
  const { data } = await apiClient.post<DiagnoseEnvelope>(
    '/api/diagnose/',
    { claim, ...opts },
    { timeout: 180000 }
  );
  return data;
}


export interface GraphNode {
  id: string; model: string; metric: string; model_label?: string;
  role: 'lever' | 'outcome' | 'intermediate' | 'isolated';
  degree: number;
  /** how a reading of this metric should be formatted */
  type?: string; unit?: string;
}

/** One week-over-week reading, over a window anchored to the data itself. */
export interface GraphStat {
  value: number;
  prior: number | null;
  delta_pct: number | null;
  window_start: string; window_end: string;
  prior_window_start: string; prior_window_end: string;
}
export interface GraphEdge {
  source: string; target: string; kind: string; status: string;
  confidence: number | null; provenance: Record<string, unknown>;
  lag_days?: number | null;
}

/** Workspace metric dependency graph (why engine, #138). */
export async function getMetricGraph(): Promise<{ nodes: GraphNode[]; edges: GraphEdge[]; warnings: string[] }> {
  // NB: the response interceptor already unwraps { success, data }
  const { data } = await apiClient.get<{ nodes: GraphNode[]; edges: GraphEdge[]; warnings: string[] }>('/api/graph/');
  return data;
}

/**
 * Week-over-week readings for the graph's metrics (#138).
 *
 * Separate from getMetricGraph because it costs ~3 warehouse queries per
 * model — the canvas draws first, the numbers arrive after. Models that
 * cannot be measured come back in `warnings`, never as a failure.
 */
/** One event on the why-engine timeline (#111). */
export interface Annotation {
  id: string;
  kind: 'deploy' | 'experiment' | 'incident' | 'campaign' | 'manual' | 'note' | 'correction' | 'refresh' | 'contribution' | 'pipeline_run' | string;
  /** effective (rollout) time, never merge time */
  ts: string;
  ts_end?: string | null;
  title: string;
  body?: string | null;
  source: string;
  source_ref?: string | null;
  /** {metrics?, domains?, dims?} — raises rank, never filters */
  scope: { metrics?: string[]; domains?: string[]; dims?: Record<string, unknown>; perspective?: string };
  confidence: number;
  /** Server-stamped: asserted_by, asserted_by_name, asserted_at, method — and `correction` for kind correction (#186). */
  provenance?: Record<string, any>;
}

/**
 * The event timeline (#111). Unscoped events are legitimate and common —
 * time overlap is the only match requirement, scope only raises rank — so
 * this returns everything in range and the caller decides what to highlight.
 */
/** List annotations with the plain filters (kind, source, since, until). */
export async function listAnnotations(params?: {
  kind?: string; source?: string; source_ref?: string | string[]; since?: string; until?: string; limit?: number;
  /** ranked mode (#183): the notes ABOUT this metric over this window, however they were
   *  addressed — a Slack or MCP note carries a scope, not a claim key, and is found only here */
  metric?: string; perspective?: string; filters?: string; row?: string; window_start?: string; window_end?: string;
}): Promise<Annotation[]> {
  const { data } = await apiClient.get<Annotation[]>('/api/annotations/', { params, paramsSerializer: { indexes: null } });
  return data;
}

/** Create an annotation. `asserted_by`/`asserted_at` are server-stamped. */
export async function createAnnotation(body: Partial<Annotation> & Record<string, any>): Promise<Annotation> {
  const { data } = await apiClient.post<Annotation>('/api/annotations/', body);
  return data;
}

export async function getAnnotation(id: string): Promise<Annotation> {
  const { data } = await apiClient.get<Annotation>(`/api/annotations/${encodeURIComponent(id)}`);
  return data;
}

/** What `POST /validate` answers (#193): the record, whether THIS call started a run, and — when it did not
 *  because one is under way — the seconds until the server would restart it. */
export interface ValidateContributionResult { row: Annotation; started: boolean; retryAfterS: number }

/** Run (or re-run) a contribution's dry run (#193). Idempotent: a run under way is not started twice. */
export async function validateContribution(id: string): Promise<ValidateContributionResult> {
  const { data } = await apiClient.post<{ annotation: Annotation; started?: boolean; retry_after_s?: number }>(
    `/api/annotations/${encodeURIComponent(id)}/validate`, {});
  return { row: data.annotation, started: data.started === true, retryAfterS: Number(data.retry_after_s) || 0 };
}

/** A browser holding a token for the extension (#195); the value itself rides on the minting answer only. */
export interface ExtensionToken {
  id: string; name: string; created_at?: string | null; expires_at?: string | null; last_used_at?: string | null; revoked_at?: string | null; live: boolean;
}

export async function mintExtensionToken(name: string): Promise<ExtensionToken & { token: string }> {
  const { data } = await apiClient.post<ExtensionToken & { token: string }>('/api/auth/extension-tokens', { name });
  return data;
}

/** The extension ids this deployment trusts (#195); a presence message from any other id is not Voyager. */
export async function getExtensionConfig(): Promise<{ extension_ids: string[] }> {
  const { data } = await apiClient.get<{ extension_ids: string[] }>('/api/auth/extension-tokens/config');
  return data;
}

export async function listExtensionTokens(): Promise<ExtensionToken[]> {
  const { data } = await apiClient.get<ExtensionToken[]>('/api/auth/extension-tokens');
  return data;
}

export async function revokeExtensionToken(id: string): Promise<void> {
  await apiClient.delete(`/api/auth/extension-tokens/${encodeURIComponent(id)}`);
}

// ---- re-grounding, remembered per source (#196) ----

export interface SourceMapping {
  id: string; asserted_by?: string | null; asserted_by_name?: string | null; asserted_at?: string | null;
  metric_qid?: string; perspective?: string; filters?: Array<{ dimension: string; operator?: string; value: any }>;
  window_rule?: { kind: string; days?: number } | null; source_kind?: string; source_ref?: string; label?: string;
  owner_id?: string; created_at?: string | null; revoked_at?: string | null; revoked_by?: Record<string, any> | null; live?: boolean;
}

export interface AdjustBody {
  /** the number in the app: a saved review's section is the only identity a mapping is kept on */
  artifact?: { kind: 'review'; thread_id: string; section_id: string } | null;
  page?: { url: string; kind: string; title?: string; window?: { start: string; end: string } | null; sender?: string | null; subject?: string | null } | null;
  claim: { id?: string; label: string; rendered?: string | null; value?: number | null; unit?: string | null; anchor?: any; column?: string | null; window?: { start: string; end: string } | null; group?: any; comparison?: string | null };
  grounding: { metric_qid: string; perspective: string; filters?: Array<{ dimension: string; operator?: string; value: any }>; window?: { start: string; end: string } | null; window_rule?: { kind: string; days?: number } | null };
  remember?: boolean;
  use_definitions?: string[];
}

export interface AdjustResult {
  claim: { id: string; label: string; verdict: 'match' | 'stale' | 'unknown'; reason?: string | null; resolved_by?: string | null; mapping?: SourceMapping | null;
           grounding?: Record<string, any> | null; claim?: Record<string, any> | null; reconcile?: any; rendered?: string | null; value?: number | null };
  mapping: SourceMapping | null; identity: { kind: string; ref: string } | null; remembered: boolean; note?: string | null; window?: { start: string; end: string } | null;
}

/** Re-ground one number under a correction (#196) — and remember it for its source when `remember` is set. */
export async function adjustClaim(body: AdjustBody): Promise<AdjustResult> {
  const { data } = await apiClient.post<AdjustResult>('/api/claims/adjust', body, { timeout: RECONCILE_TIMEOUT_MS });
  return data;
}

export async function listSourceMappings(params?: { source_kind?: string; source_ref?: string; include_revoked?: boolean }): Promise<SourceMapping[]> {
  const { data } = await apiClient.get<SourceMapping[]>('/api/source-mappings', { params });
  return data;
}

export async function revokeSourceMapping(id: string): Promise<SourceMapping> {
  const { data } = await apiClient.delete<SourceMapping>(`/api/source-mappings/${encodeURIComponent(id)}`);
  return data;
}

export async function getAnnotations(params?: { since?: string; limit?: number }): Promise<Annotation[]> {
  const { data } = await apiClient.get<Annotation[]>('/api/annotations/', {
    params: { limit: params?.limit ?? 500, ...(params?.since ? { since: params.since } : {}) },
  });
  return data;
}

/** One component of why an event scored against a metric (#111). */
export interface MatchReasonPart { reason: string; detail: string; points: number }
export interface MatchReasons {
  parts: MatchReasonPart[];
  subtotal: number; confidence: number; total: number;
  /** false = matched on time alone, which is legitimate and common */
  routed: boolean;
}
export type RankedAnnotation = Annotation & {
  match_score: number; match_reasons: MatchReasons;
};

/**
 * Events ranked for one metric over a window, each carrying the backend's own
 * score breakdown. The UI shows that breakdown as the trace for "why is this
 * event attached to this metric" — re-deriving the rule on the client would
 * be a second copy that drifts away from `annotation_match`.
 */
export async function getRankedAnnotations(
  metric: string, windowStart: string, windowEnd: string,
): Promise<RankedAnnotation[]> {
  const { data } = await apiClient.get<RankedAnnotation[]>('/api/annotations/', {
    params: { metric, window_start: windowStart, window_end: windowEnd, limit: 500 },
  });
  return data;
}

export async function getMetricGraphStats(): Promise<{ stats: Record<string, GraphStat>; warnings: string[] }> {
  // Up to three warehouse queries per model. The client default of 30s would
  // have the browser discard the response while the server was still running
  // the queries — every pill blank, and the work done for nobody.
  const { data } = await apiClient.get<{ stats: Record<string, GraphStat>; warnings: string[] }>(
    '/api/graph/stats', { timeout: 120000 });
  return data;
}

// ============================================================
// Threads (#181) — durable conversations: list, persist, star, close, reopen
// ============================================================

/** The wire shape of a thread row. `snapshot.turns` is the transcript. */
export interface ThreadRow {
  id: string;
  kind: 'thread' | 'doc' | 'agent_chat' | 'group';
  title: string;
  preview: string;
  perspective_id: string | null;
  domain_id: string | null;
  space_id: string | null;
  starred: boolean;
  closed_at: string | null;
  last_opened_at: string | null;
  tab_order: number;
  source_kind: string | null;
  source_id: string | null;
  /** The objective the thread is scoped to (#766): its questions are asked inside it. */
  objective_id?: string | null;
  snapshot: { turns?: any[] } | Record<string, any>;
  /** A review document's content (#189); `{}` for a conversation or an unstarted review. */
  doc?: ReviewDoc | Record<string, never> | null;
  created_at: string;
  updated_at: string;
}

export type ThreadState = 'open' | 'starred' | 'history' | 'all';

/** The fields a client may write. Everything else is server-managed. */
export interface ThreadWrite {
  kind?: 'thread' | 'doc' | 'agent_chat' | 'group';
  title?: string;
  preview?: string;
  perspective_id?: string | null;
  domain_id?: string | null;
  space_id?: string | null;
  starred?: boolean;
  tab_order?: number;
  source_kind?: string | null;
  source_id?: string | null;
  objective_id?: string | null;
  snapshot?: { turns: any[] };
}

export function threadRowToConversation(row: ThreadRow): ConversationThread {
  const snapshot: any = row.snapshot || {};
  return {
    id: row.id,
    title: row.title || 'Conversation',
    preview: row.preview || '',
    messages: [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    spaceId: row.space_id ?? null,
    detectedPerspective: row.perspective_id ?? null,
    detectedDomain: row.domain_id ?? null,
    landingAgentTurns: Array.isArray(snapshot.turns) ? snapshot.turns : [],
    perspectiveId: row.perspective_id ?? null,
    domainId: row.domain_id ?? null,
    kind: row.kind || 'thread',
    starred: Boolean(row.starred),
    closedAt: row.closed_at ?? null,
    lastOpenedAt: row.last_opened_at ?? null,
    tabOrder: row.tab_order ?? 0,
    sourceKind: row.source_kind ?? null,
    sourceId: row.source_id ?? null,
    objectiveId: row.objective_id ?? null,
    doc: (row.doc && typeof row.doc === 'object') ? (row.doc as ReviewDoc) : null,
  };
}

/**
 * One page of threads, newest change first. `before` is the cursor of the
 * last row seen (`threadCursor`); a page shorter than `limit` is the last.
 */
export async function listThreads(
  state: ThreadState = 'all', limit = 200, before?: string,
): Promise<ThreadRow[]> {
  const { data } = await apiClient.get<ThreadRow[]>('/api/threads', {
    params: { state, limit, ...(before ? { before } : {}) },
  });
  return data;
}

/** The keyset cursor for a row: `<updated_at>|<id>`. */
export function threadCursor(row: ThreadRow): string {
  return `${row.updated_at}|${row.id}`;
}

/** Create the thread, or update the caller's own — the id is client-minted. */
export async function upsertThread(id: string, body: ThreadWrite): Promise<ThreadRow> {
  const { data } = await apiClient.post<ThreadRow>('/api/threads', { id, ...body });
  return data;
}

/** A long thread's read renders every answer for this reader: it gets longer than the client's default. */
export const THREAD_READ_TIMEOUT_MS = 180000;

export async function getThread(id: string): Promise<ThreadRow> {
  const { data } = await apiClient.get<ThreadRow>(`/api/threads/${id}`, { timeout: THREAD_READ_TIMEOUT_MS });
  return data;
}

export async function patchThread(id: string, body: ThreadWrite): Promise<ThreadRow> {
  const { data } = await apiClient.patch<ThreadRow>(`/api/threads/${id}`, body);
  return data;
}

export async function closeThread(id: string): Promise<ThreadRow> {
  const { data } = await apiClient.post<ThreadRow>(`/api/threads/${id}/close`);
  return data;
}

export async function reopenThread(id: string): Promise<ThreadRow> {
  const { data } = await apiClient.post<ThreadRow>(`/api/threads/${id}/reopen`);
  return data;
}

export async function touchThread(id: string): Promise<ThreadRow> {
  const { data } = await apiClient.post<ThreadRow>(`/api/threads/${id}/touch`);
  return data;
}

export async function deleteThread(id: string): Promise<void> {
  await apiClient.delete(`/api/threads/${id}`);
}

// ---- review documents (#189) ----------------------------------------------

export interface ReviewPinBody {
  kind: 'claim' | 'suggestion';
  key: string;
  claim?: any;
  suggestion?: any;
  /** A suggestion's perspective — its thread's; a claim carries its own in the grounding. */
  perspective?: string;
  source?: { thread_id?: string | null; thread_title?: string | null; session_id?: string | null; ref?: string | null };
  commentary?: string;
}

export interface ReviewTemplate {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

/** Start a review from a template: the doc gets the template's layout and a fixed grounding. */
export async function startReviewDoc(id: string, body: { template_id: string; perspective_id: string; perspectives?: string[]; source?: ReportSource | null; window_rule?: ReviewWindowRule }): Promise<ThreadRow> {
  const { data } = await apiClient.post<ThreadRow>(`/api/threads/${id}/doc`, body);
  return data;
}

/** Pin a number or a WHAT NEXT suggestion into a review. The same key twice is one section. */
export async function pinToReview(id: string, body: ReviewPinBody): Promise<{ thread: ThreadRow; section: ReviewSection; created: boolean }> {
  const { data } = await apiClient.post<{ thread: ThreadRow; section: ReviewSection; created: boolean }>(`/api/threads/${id}/sections`, body);
  return data;
}

export async function unpinReviewSection(id: string, sectionId: string): Promise<ThreadRow> {
  const { data } = await apiClient.delete<ThreadRow>(`/api/threads/${id}/sections/${sectionId}`);
  return data;
}

export async function patchReviewSection(id: string, sectionId: string, commentary: string): Promise<ThreadRow> {
  const { data } = await apiClient.patch<ThreadRow>(`/api/threads/${id}/sections/${sectionId}`, { commentary });
  return data;
}

/** Move a tile under another of its template's groups (#568); null = the first. */
export async function patchReviewSectionGroup(id: string, sectionId: string, group: string | null): Promise<ThreadRow> {
  const { data } = await apiClient.patch<ThreadRow>(`/api/threads/${id}/sections/${sectionId}`, { group });
  return data;
}

export async function putReviewCommentary(id: string, commentary: string): Promise<ThreadRow> {
  const { data } = await apiClient.put<ThreadRow>(`/api/threads/${id}/commentary`, { commentary });
  return data;
}

export async function putReviewWindowRule(id: string, windowRule: ReviewWindowRule | null): Promise<ThreadRow> {
  const { data } = await apiClient.put<ThreadRow>(`/api/threads/${id}/window-rule`, { window_rule: windowRule });
  return data;
}

/** Weekday + time (UTC) → the review-owned workflow; `null` clears the schedule. */
export async function scheduleReview(id: string, schedule: ReviewSchedule | null): Promise<ThreadRow> {
  const { data } = await apiClient.post<ThreadRow>(`/api/threads/${id}/schedule`, schedule || {});
  return data;
}

/** A refresh re-derives every pinned number and walks the movers, synchronously: minutes, not the client's usual 30 s. */
export const REVIEW_REFRESH_TIMEOUT_MS = 300_000;

/** Run the review's refresh now. `asOf` (YYYY-MM-DD) pins the run's "today". */
export async function refreshReview(id: string, asOf?: string | null): Promise<ThreadRow & { execution?: { id: string; status: string; error?: string | null; session_id?: string | null } }> {
  const { data } = await apiClient.post<ThreadRow & { execution?: any }>(
    `/api/threads/${id}/refresh`, asOf ? { as_of: asOf } : {}, { timeout: REVIEW_REFRESH_TIMEOUT_MS },
  );
  return data;
}

// ---- reconcile (#190) -------------------------------------------------------

/** Grade extracted claims against the warehouse. Minutes at worst — every candidate is a query. */
export const RECONCILE_TIMEOUT_MS = 300_000;

export async function runReconcile(body: {
  /** the review tab the grading is done in — its trace session is bound to it */
  thread_id?: string | null;
  claims: ReconcileInputClaim[];
  window?: { start: string; end: string } | null;
  perspective?: string | null;
  use_definitions?: string[];
  /** `id`: the reader's content hash of the artifact (#568) — an edited deck under the same name is another artifact */
  artifact?: { name?: string; kind?: string; file?: string; week?: string | null; id?: string | null } | null;
}): Promise<ReconcileResult> {
  const { data } = await apiClient.post<ReconcileResult>('/api/reconcile', body, { timeout: RECONCILE_TIMEOUT_MS });
  return data;
}

/** The checked-in sample artifacts (demo data) a reconcile can start from without an upload. */
export async function listReconcileSamples(): Promise<Array<{ id: string; name: string; kind: string; perspective: string; available: boolean }>> {
  const { data } = await apiClient.get<Array<{ id: string; name: string; kind: string; perspective: string; available: boolean }>>('/api/reconcile/samples');
  return data;
}

export async function getReconcileSample(id: string): Promise<ReconcileSample> {
  const { data } = await apiClient.get<ReconcileSample>(`/api/reconcile/samples/${id}`);
  return data;
}

/** The artifact's file types the importer reads (#191). */
export const IMPORT_ACCEPT = '.pptx,.pdf,.xlsx';

/**
 * Deck import (#191): every number the artifact printed, read by the server
 * and returned in the shape reconcile grades. The file is the body; nothing
 * is stored.
 */
export async function importArtifact(file: File): Promise<ReconcileSample> {
  const { data } = await apiClient.post<ReconcileSample>(`/api/reviews/import?filename=${encodeURIComponent(file.name)}`, file,
    { headers: { 'Content-Type': 'application/octet-stream' }, timeout: RECONCILE_TIMEOUT_MS });
  return data;
}

/** The files the composer attaches (#763): what the Document agent reads. */
export const ATTACH_ACCEPT = '.pptx,.pdf,.xlsx,.csv';

/** What an ask carries for a file attached in the composer (the router's `Attachment`). */
export interface Attachment { upload_id: string; name: string; media_type: string | null }

/** Keep a file for the ask that attaches it; only its uploader can read it back. */
export async function uploadDocument(file: File): Promise<Attachment> {
  const { data } = await apiClient.post<Attachment & { size: number }>(
    `/api/documents/uploads?filename=${encodeURIComponent(file.name)}`, file,
    { headers: { 'Content-Type': 'application/octet-stream' }, timeout: RECONCILE_TIMEOUT_MS });
  return { upload_id: data.upload_id, name: data.name, media_type: data.media_type };
}

/** What choosing one number of an answered document shows (#763). */
export type DocumentPick =
  | { key: string; kind: 'grounded' | 'mismatch'; metric: string; candidate: string; metric_qid: string; perspective: string | null; perspective_name: string | null; dimension: string | null; deck: string; warehouse: string | null; chosen: boolean; claim: Record<string, any> | null; note?: string | null }
  | { key: string; kind: 'clarify'; prompt: string; options: Array<{ candidate: string; metric_qid: string; metric: string; dimension: string | null; perspective_name: string | null; rendered: string | null; match: string }> }
  | { key: string; kind: 'define'; question: string; request: Record<string, any> | null }
  | { key: string; kind: 'unchecked'; reason: string };

/** `candidate` names one metric (by its dimension, for a cell of a grouped table) the asker chose for the number. */
export async function pickDocumentNumber(sessionId: string, key: string, candidate?: string | null): Promise<DocumentPick> {
  const { data } = await apiClient.post<DocumentPick>('/api/documents/pick', { session_id: sessionId, key, candidate: candidate || null });
  return data;
}

/** Who an upload is shared with (#763 part B1) — its uploader's view. */
export interface DocumentWidening {
  shared_with: string[];
  /** Where its Add to Knowledge request stands (#763 part B2), with the reason a decline gave. */
  publication: { state: 'pending' | 'approved' | 'rejected' | 'retired'; reason: string | null; by: string | null; at: string } | null;
  /** A workspace admin's own request is approved on submit. */
  approves_on_submit: boolean;
}

export async function readDocumentWidening(uploadId: string): Promise<DocumentWidening> {
  return (await apiClient.get<DocumentWidening>(`/api/documents/${uploadId}/widening`)).data;
}

/** Share with people: workspace members by e-mail address, at once; signed and revocable. */
export async function shareDocument(uploadId: string, readers: string[]): Promise<DocumentWidening> {
  return (await apiClient.post<DocumentWidening>(`/api/documents/${uploadId}/share`, { readers })).data;
}

export async function unshareDocument(uploadId: string, reader: string): Promise<DocumentWidening> {
  return (await apiClient.post<DocumentWidening>(`/api/documents/${uploadId}/unshare`, { reader })).data;
}

/** Add to Knowledge (#763 part B2): a request from the answer that read the upload — an admin's is approved on submit. */
export async function requestDocumentPublication(uploadId: string, sessionId: string): Promise<DocumentWidening> {
  return (await apiClient.post<DocumentWidening>(`/api/documents/${uploadId}/publish`, { session_id: sessionId })).data;
}

/** Remove from Knowledge (the uploader, or a workspace admin). */
export async function retireDocumentPublication(uploadId: string): Promise<DocumentWidening> {
  return (await apiClient.post<DocumentWidening>(`/api/documents/${uploadId}/retire`, {})).data;
}

/** A workspace admin's decision on a request in Knowledge › Needs review, on the revision its card showed
 *  (`contentHash`) — refused if that is no longer the one waiting; a decline gives its reason. */
export async function decideDocumentPublication(uploadId: string, contentHash: string, decision: 'approve' | 'decline', reason?: string): Promise<{ state: string }> {
  return (await apiClient.post<{ state: string }>(`/api/documents/${uploadId}/${decision}`,
    decision === 'decline' ? { content_hash: contentHash, reason } : { content_hash: contentHash })).data;
}

/** The checked-in demo deck, read by the same importer an upload goes through. */
export async function getImportSample(id: string): Promise<ReconcileSample> {
  const { data } = await apiClient.get<ReconcileSample>(`/api/reviews/import/samples/${id}`);
  return data;
}

/** Live reports (#192): the demo report on the deployment's own Tableau site, and a report connection's scorecard. */
export async function listLiveReportSamples(): Promise<Array<{ id: string; name: string; kind: string; provider: string; available: boolean }>> {
  const { data } = await apiClient.get<Array<{ id: string; name: string; kind: string; provider: string; available: boolean }>>('/api/reviews/live/samples');
  return data;
}

export async function getLiveReportSample(id: string): Promise<ReconcileSample> {
  const { data } = await apiClient.get<ReconcileSample>(`/api/reviews/live/samples/${id}`, { timeout: RECONCILE_TIMEOUT_MS });
  return data;
}

export async function readLiveReport(connectionId: string): Promise<ReconcileSample> {
  const { data } = await apiClient.post<ReconcileSample>(`/api/reviews/live/${connectionId}`, {}, { timeout: RECONCILE_TIMEOUT_MS });
  return data;
}

/** The document templates a review can start from (the workflow YAML templates). */
export async function listReviewTemplates(): Promise<ReviewTemplate[]> {
  const { data } = await apiClient.get<ReviewTemplate[]>('/api/workflows/templates');
  return data;
}

// ============================================================
// Definitions (#151) — business terms captured from conversation
// ============================================================

export interface DefinitionRow {
  id: string;
  name: string;
  display_name: string;
  kind: 'metric' | 'dimension' | 'term' | 'cohort';
  perspective: string;
  model_name?: string | null;
  english: string;
  formula: string;
  value_type: 'number' | 'integer' | 'currency' | 'percentage';
  status: 'draft' | 'published' | 'needs_changes' | 'requested' | 'answered';
  answered_by_definition_id?: string | null;
  can_dismiss?: boolean;
  /** #1509, flag on only: the review gate would let this viewer publish this draft */
  can_review?: boolean;
  confidence?: number | null;
  provenance?: Record<string, any>;
  validation?: DefinitionValidation;
  review_note?: string | null;
  reviewed_by?: string | null;
  author_name?: string;
  owner_id?: string;
  created_at?: string;
  updated_at?: string;
  mine?: boolean;
}

export interface DefinitionValidation {
  ok: boolean;
  sql?: string;
  columns?: string[];
  rows?: any[];
  row_count?: number;
  execution_time_ms?: number;
  dimension?: string | null;
  empty?: boolean;
  error?: string;
  phase?: string;
}

export interface DefinitionCandidate {
  name: string;
  display_name: string;
  kind: string;
  perspective: string;
  model_name?: string | null;
  english: string;
  formula: string;
  value_type: string;
  confidence?: number | null;
  notes?: string[];
  author_name?: string;
  provenance?: Record<string, any>;
}

export interface DefinitionProposal {
  candidate: DefinitionCandidate;
  validation: DefinitionValidation | null;
  existing: DefinitionRow | null;
  nearby: { name: string; display_name?: string; description?: string }[];
}

export async function listDefinitions(params?: {
  perspective?: string; status?: string;
}): Promise<DefinitionRow[]> {
  const { data } = await apiClient.get<DefinitionRow[]>('/api/definitions', { params });
  return data;
}

/** Draft + dry-run a definition. Writes nothing — the caller approves first. */
export async function proposeDefinition(body: {
  term: string;
  perspective: string;
  kind?: string;
  english?: string;
  blocks?: any;
  sql?: string;
  breakdown?: string;
  provenance?: Record<string, any>;
}): Promise<DefinitionProposal> {
  const { data } = await apiClient.post<DefinitionProposal>(
    '/api/definitions/propose',
    { ...body, channel: 'web' },
    // A dry run runs a real warehouse query; the shared 30s ceiling is too
    // tight for a cold BigQuery slot and surfaces as a spurious failure.
    { timeout: 120000 },
  );
  return data;
}

export async function saveDefinition(
  candidate: DefinitionCandidate,
  validation?: DefinitionValidation | null,
  allowEmpty = false,
): Promise<DefinitionRow> {
  // `validation` is informational — the server re-runs the dry run and stores
  // its own result. `allowEmpty` is the user's answer to the "returned no
  // rows" warning, which is why only a surface that showed it may send true.
  const { data } = await apiClient.post<DefinitionRow>('/api/definitions',
    { candidate, validation, allow_empty: allowEmpty }, { timeout: 120000 });
  return data;
}

/** Re-propose an existing definition with a new meaning. Writes nothing. */
export async function reviseDefinition(id: string, body: {
  english?: string; sql?: string; blocks?: any;
}): Promise<DefinitionProposal> {
  const { data } = await apiClient.post<DefinitionProposal>(
    `/api/definitions/${id}/revise`, body, { timeout: 120000 });
  return data;
}

export async function updateDefinition(
  id: string, body: Partial<DefinitionCandidate>,
): Promise<DefinitionRow> {
  const { data } = await apiClient.put<DefinitionRow>(`/api/definitions/${id}`, body);
  return data;
}

export async function publishDefinition(
  id: string, note = '', expectedVersion?: string | null,
): Promise<DefinitionRow> {
  // `expectedVersion` is the `updated_at` of the row the reviewer actually
  // read. The server checks it inside the UPDATE, so an author edit landing
  // mid-review can't be published under the reviewer's name.
  const { data } = await apiClient.post<DefinitionRow>(
    `/api/definitions/${id}/publish`, { note, expected_version: expectedVersion });
  return data;
}

export async function requestDefinitionChanges(id: string, note: string): Promise<DefinitionRow> {
  const { data } = await apiClient.post<DefinitionRow>(
    `/api/definitions/${id}/request-changes`, { note });
  return data;
}

export async function revalidateDefinition(id: string): Promise<DefinitionValidation> {
  const { data } = await apiClient.post<DefinitionValidation>(
    `/api/definitions/${id}/revalidate`, {}, { timeout: 120000 });
  return data;
}

export async function deleteDefinition(id: string): Promise<void> {
  await apiClient.delete(`/api/definitions/${id}`);
}

export default apiClient;

/** A composed answer keeps readable text beside the validated slot template. */
export type AgentClause = {
  slot: string;
  text: string;
  tier: import('./types/knowledge').Tier;
  sources: import('./types/knowledge').SourceRef[];
} & ({ kind: 'number'; claim: import('./components/Ladder/claims').Claim & { session_id: string | null } }
  | { kind: 'quote'; check?: { verdict: 'match' | 'off' | 'unverifiable'; measured: number | null }; span: import('./types/knowledge').Span & { quote: string } }
  /** How much of a cut the answer's cells are (#368) — bound to the measurement, carried by no claim of its own. */
  | { kind: 'coverage' | 'relationship' });

export interface ComposedAgentReply {
  kind: 'answer' | 'refused' | 'handoff';
  text: string;
  template?: string;
  reason?: string;
  clauses: AgentClause[];
  made: { agent: string; briefing_at: string | null; warehouse_at: string | null; bar: string; gated: boolean };
  session_id: string;
  promise_id?: string;
}

export type KnowledgeFacet = 'person' | 'entity' | 'metric' | 'source';
export type AgentKnowledgeRow = Pick<import('./types/knowledge').Claim, 'quote' | 'speaker' | 'tier' | 'at' | 'span' | 'metric' | 'value'> & {
  claim_id: string;
  source: import('./types/knowledge').KnowledgeSource;
  document_title: string;
  entities: string[];
  created_at?: string;
};
export interface AgentKnowledgeDossier {
  by: KnowledgeFacet;
  groups: Array<{ key: string; label: string; count: number; rows: AgentKnowledgeRow[] }>;
  hidden: { policy: number };
  raise_to: string | null;
  changed: { since: string | null; rows: AgentKnowledgeRow[] };
  waiting_on: Array<{ kind: 'promise'; label: string; ref: string; promised_at: string } |
    { kind: 'source' | 'person'; label: string; ref: string }>;
  made: ComposedAgentReply['made'];
  session_id: string;
}


export async function requestDefinition(body: {
  term: string; perspective: string; kind?: string; english?: string; sql?: string;
  provenance?: Record<string, any>;
}): Promise<{ definition: DefinitionRow; message: string }> {
  const { data } = await apiClient.post<{ definition: DefinitionRow; message: string }>(
    '/api/definitions/request', body);
  return data;
}

export async function definitionCaptureContext(term: string, perspective: string) {
  const { data } = await apiClient.get<import('./stores/slices/definitionsSlice').DefinitionRequest>(
    '/api/definitions/capture', { params: { term, perspective } });
  return data;
}
